"use client"

import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../lib/utils"

// ============================================================================
// Typography Variants (shadcn 기반)
// ============================================================================

const typographyVariants = cva(
  "text-foreground",
  {
    variants: {
      variant: {
        // Headings
        h1: "scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl",
        h2: "scroll-m-20 text-3xl font-semibold tracking-tight",
        h3: "scroll-m-20 text-2xl font-semibold tracking-tight",
        h4: "scroll-m-20 text-xl font-semibold tracking-tight",
        h5: "scroll-m-20 text-lg font-semibold tracking-tight",
        h6: "scroll-m-20 text-base font-semibold tracking-tight",

        // Body text
        p: "leading-7",
        body1: "text-base leading-7",
        body2: "text-sm leading-6",

        // Special text
        subtitle1: "text-lg font-medium leading-7",
        subtitle2: "text-base font-medium leading-6",
        caption: "text-xs leading-5",
        overline: "text-xs font-medium uppercase tracking-wider leading-5",

        // Semantic elements
        blockquote: "mt-6 border-l-2 pl-6 italic",
        code: "relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold",
        kbd: "pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100",
        mark: "bg-yellow-200 dark:bg-yellow-800 px-1 rounded",

        // Lists
        list: "my-6 ml-6 [&>li]:mt-2",
        listItem: "mt-2",

        // Links and inline
        link: "font-medium text-primary underline underline-offset-4 hover:no-underline",
        small: "text-sm font-medium leading-none",
        large: "text-lg font-semibold",
        lead: "text-xl text-muted-foreground",
        muted: "text-sm text-muted-foreground",

        // Inline decorative (no margins, no block styling — inherits size AND color)
        inline: "",
        // Inline + muted color (inherits size from parent, muted foreground for secondary text in tables/cells)
        inlineMuted: "text-muted-foreground",
      },
      size: {
        xs: "text-xs",
        sm: "text-sm",
        default: "text-base",
        lg: "text-lg",
        xl: "text-xl",
        "2xl": "text-2xl",
        "3xl": "text-3xl",
        "4xl": "text-4xl",
      },
      weight: {
        light: "font-light",
        normal: "font-normal",
        medium: "font-medium",
        semibold: "font-semibold",
        bold: "font-bold",
        extrabold: "font-extrabold",
      },
      color: {
        default: "text-foreground",
        inherit: "", // No text color class - inherit from parent
        muted: "text-muted-foreground",
        accent: "text-accent-foreground",
        primary: "text-primary",
        success: "text-green-600 dark:text-green-400",
        warning: "text-yellow-600 dark:text-yellow-400",
        destructive: "text-destructive",
        inverse: "text-background",
      },
      align: {
        left: "text-left",
        center: "text-center",
        right: "text-right",
        justify: "text-justify",
      },
      transform: {
        none: "normal-case",
        uppercase: "uppercase",
        lowercase: "lowercase",
        capitalize: "capitalize",
      },
    },
    defaultVariants: {
      variant: "p",
      size: "default",
      weight: "normal",
      color: "default",
      align: "left",
      transform: "none",
    },
  }
)

// ============================================================================
// Typography Component Types (Simplified)
// ============================================================================

export interface UITypographyProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'color'>,
          VariantProps<typeof typographyVariants> {
  /**
   * HTML 요소 타입 (polymorphic)
   */
  as?: React.ElementType
  /**
   * 텍스트 말줄임 처리
   * true: 1줄 말줄임, number: 여러 줄 말줄임
   */
  truncate?: boolean | number
  /**
   * 텍스트 선택 가능 여부
   */
  selectable?: boolean
  /**
   * 자식 요소 (텍스트 콘텐츠)
   */
  children: React.ReactNode
}

// ============================================================================
// Main Typography Component
// ============================================================================

/**
 * UITypography - 통합 Typography 컴포넌트
 *
 * shadcn Typography 시스템을 기반으로 한 완전한 텍스트 표시 컴포넌트입니다.
 *
 * @example
 * ```tsx
 * // 기본 단락
 * <UITypography>Hello World</UITypography>
 *
 * // 제목 요소
 * <UITypography as="h1" variant="h1">Page Title</UITypography>
 *
 * // 커스텀 스타일링
 * <UITypography
 *   variant="subtitle1"
 *   color="accent"
 *   align="center"
 * >
 *   Subtitle Text
 * </UITypography>
 *
 * // 말줄임 처리
 * <UITypography truncate>Very long text that will be truncated...</UITypography>
 * ```
 */
export const UITypography = React.forwardRef<
  HTMLElement,
  UITypographyProps
>(({
  as = "p",
  variant,
  size,
  weight,
  color,
  align,
  transform,
  truncate,
  selectable = true,
  className,
  children,
  ...props
}, ref) => {
  const Component = as as any

  // 말줄임 클래스 생성
  const getTruncateClasses = () => {
    if (truncate === true) return "truncate"
    if (typeof truncate === "number" && truncate > 0) {
      return `line-clamp-${Math.min(truncate, 12)}` // 최대 12줄까지 지원
    }
    return ""
  }

  return (
    <Component
      ref={ref}
      className={cn(
        typographyVariants({
          variant,
          size,
          weight,
          color,
          align,
          transform,
        }),
        getTruncateClasses(),
        !selectable && "select-none",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  )
})

UITypography.displayName = "UITypography"

// ============================================================================
// Preset Typography Components
// ============================================================================

// Heading Components
export interface UIHeadingProps extends Omit<UITypographyProps, "variant"> {}

export const UIHeading1 = React.forwardRef<
  HTMLHeadingElement,
  UIHeadingProps
>(({ children, ...props }, ref) => (
  <UITypography as="h1" variant="h1" ref={ref} {...props}>
    {children}
  </UITypography>
))
UIHeading1.displayName = "UIHeading1"

export const UIHeading2 = React.forwardRef<
  HTMLHeadingElement,
  UIHeadingProps
>(({ children, ...props }, ref) => (
  <UITypography as="h2" variant="h2" ref={ref} {...props}>
    {children}
  </UITypography>
))
UIHeading2.displayName = "UIHeading2"

export const UIHeading3 = React.forwardRef<
  HTMLHeadingElement,
  UIHeadingProps
>(({ children, ...props }, ref) => (
  <UITypography as="h3" variant="h3" ref={ref} {...props}>
    {children}
  </UITypography>
))
UIHeading3.displayName = "UIHeading3"

export const UIHeading4 = React.forwardRef<
  HTMLHeadingElement,
  UIHeadingProps
>(({ children, ...props }, ref) => (
  <UITypography as="h4" variant="h4" ref={ref} {...props}>
    {children}
  </UITypography>
))
UIHeading4.displayName = "UIHeading4"

// Text Components
export interface UITextProps extends Omit<UITypographyProps, "variant"> {}

export const UIBody = React.forwardRef<
  HTMLParagraphElement,
  UITextProps
>(({ children, ...props }, ref) => (
  <UITypography variant="p" ref={ref} {...props}>
    {children}
  </UITypography>
))
UIBody.displayName = "UIBody"

export const UISubtitle = React.forwardRef<
  HTMLParagraphElement,
  UITextProps
>(({ children, ...props }, ref) => (
  <UITypography variant="subtitle1" ref={ref} {...props}>
    {children}
  </UITypography>
))
UISubtitle.displayName = "UISubtitle"

export const UICaption = React.forwardRef<
  HTMLSpanElement,
  UITextProps
>(({ children, ...props }, ref) => (
  <UITypography as="span" variant="caption" ref={ref} {...props}>
    {children}
  </UITypography>
))
UICaption.displayName = "UICaption"

export const UILead = React.forwardRef<
  HTMLParagraphElement,
  UITextProps
>(({ children, ...props }, ref) => (
  <UITypography variant="lead" ref={ref} {...props}>
    {children}
  </UITypography>
))
UILead.displayName = "UILead"

export const UIMuted = React.forwardRef<
  HTMLParagraphElement,
  UITextProps
>(({ children, ...props }, ref) => (
  <UITypography variant="muted" ref={ref} {...props}>
    {children}
  </UITypography>
))
UIMuted.displayName = "UIMuted"

// Special Components
export const UIBlockquote = React.forwardRef<
  HTMLQuoteElement,
  UITextProps
>(({ children, ...props }, ref) => (
  <UITypography as="blockquote" variant="blockquote" ref={ref} {...props}>
    {children}
  </UITypography>
))
UIBlockquote.displayName = "UIBlockquote"

export const UICode = React.forwardRef<
  HTMLElement,
  UITextProps
>(({ children, ...props }, ref) => (
  <UITypography as="code" variant="code" ref={ref} {...props}>
    {children}
  </UITypography>
))
UICode.displayName = "UICode"

export const UIKbd = React.forwardRef<
  HTMLElement,
  UITextProps
>(({ children, ...props }, ref) => (
  <UITypography as="kbd" variant="kbd" ref={ref} {...props}>
    {children}
  </UITypography>
))
UIKbd.displayName = "UIKbd"

// Export types and variants
export { typographyVariants }
export type TypographyElement = React.ElementType